import React, { useEffect } from 'react'
import Select from '@material-ui/core/Select'
import { SelectProps } from '@material-ui/core/Select/Select'
import { primaryBlue } from '../../styles/material-ui-theme-v2'
import { ReactComponent as SelectArrowIcon } from '../../assets/v3/icons/select-arrow.svg'
import { getWidthOfText } from '../YumManualEntryForm/YumManualEntryForm'
import { Input } from '@material-ui/core'
import { isRtl } from '../../i18nInit'
import { makeStyles } from '@material-ui/core/styles'

const useStyles = makeStyles((theme) => ({
  select: {
    padding: '0 8px',
    borderRadius: '8px !important',
    textTransform: 'none',
    fontSize: '0.875rem',
    fontWeight: 600,
    //height: '30px',
    //lineHeight: '30px',
    height: '1.875rem',
    lineHeight: '1.875rem',
  },
}))

let yumSmallSelectIdCounter = 0

interface YumSmallSelectProps extends SelectProps {
  fullWidth?: boolean
}

export default function YumSmallSelect({ children, fullWidth, ...props }: YumSmallSelectProps) {
  const classes = useStyles()
  const [width, setWidth] = React.useState(50)
  // Pull aria-label out of either caller inputProps or top-level so we can merge it into
  // the final inputProps below (and still pass it to any fallback path). If no caller
  // label is provided at all, fall back to a generic name so axe's `select-name` never
  // fails — individual call sites should still pass a better label via inputProps.
  const explicitAriaLabel =
    (props.inputProps && (props.inputProps as any)['aria-label']) || (props as any)['aria-label']
  const callerAriaLabel = explicitAriaLabel || 'Select'
  // MUI v4 Select (native + custom `input=`) drops aria-label in this project, so back it
  // up with a visually-hidden <label htmlFor=...> bound to a stable id on the native select.
  const fallbackIdRef = React.useRef<string | null>(null)
  if (!fallbackIdRef.current) {
    yumSmallSelectIdCounter += 1
    fallbackIdRef.current = `yum-small-select-${yumSmallSelectIdCounter}`
  }
  const nativeSelectId =
    (props.inputProps && (props.inputProps as any).id) || (props as any).id || fallbackIdRef.current

  const updateTextSize = () => {
    // Flatten children array to handle nested arrays and optgroups
    const flattenChildren = (children: any[]): any[] => {
      return children.reduce((acc: any[], child: any) => {
        if (Array.isArray(child)) {
          return acc.concat(flattenChildren(child))
        } else if (child && child.props && child.props.children && child.type === 'optgroup') {
          const optgroupChildren = Array.isArray(child.props.children) ? child.props.children : [child.props.children]
          return acc.concat(flattenChildren(optgroupChildren))
        } else if (child && child.props) {
          return acc.concat(child)
        }
        return acc
      }, [])
    }

    const flatChildren = flattenChildren(children || [])

    // @ts-ignore
    const text = flatChildren.find((c: { props: { value: any } }) => c?.props?.value === props.value)?.props?.children
    if (text) {
      const newWidth = getWidthOfText(text, '600', '0.875rem')
      setWidth(newWidth)
    }
  }

  useEffect(() => {
    updateTextSize()
  }, [props.value, children])

  // MUI v4's InputBase filters aria-label/title off the native <select> rendered by
  // NativeSelectInput (only `id` and `style` from inputProps survive the cloneElement chain).
  // Bypass the whole prop chain by writing the attribute directly on the DOM node after render.
  useEffect(() => {
    if (!callerAriaLabel) return
    const nativeSelect = document.getElementById(nativeSelectId)
    if (nativeSelect) {
      nativeSelect.setAttribute('aria-label', callerAriaLabel)
    }
  })

  /*componentDidUpdate(prevProps: Readonly<SelectProps>, prevState: Readonly<{}>, snapshot?: any) {
    if (props.value !== prevProps.value) {
      updateTextSize()
    }
  }*/

  const onChange = (event: React.ChangeEvent<{ name?: string; value: unknown }>) => {
    /*// @ts-ignore
    const text = event.target.options[event.target.selectedIndex].text
    console.log('YumSmallSelect onChange', text)
    if (text) setWidth(getWidthOfText(text, '700', '10px'))*/
    // @ts-ignore
    if (props.onChange) props.onChange(event)
  }

  const outlined = props.variant === 'outlined'

  return (
    <>
      {callerAriaLabel && (
        <label
          htmlFor={nativeSelectId}
          style={{
            position: 'absolute',
            width: 1,
            height: 1,
            padding: 0,
            margin: -1,
            overflow: 'hidden',
            clip: 'rect(0,0,0,0)',
            whiteSpace: 'nowrap',
            border: 0,
          }}
        >
          {callerAriaLabel}
        </label>
      )}
      <Select
        {...props}
        onChange={onChange}
        native
        IconComponent={(props) => (
          <SelectArrowIcon
            {...props}
            style={{
              width: '0.625rem',
              height: '0.75rem',
              top: 'auto',
              left: isRtl ? '0.625rem' : 'auto',
              right: isRtl ? 'auto' : '0.625rem',
              color: outlined ? primaryBlue : 'white',
            }}
          />
        )}
        style={{
          ...props.style,
          //width: fullWidth ? '100%' : width + 39 + 'px',
          width: fullWidth ? '100%' : `calc(${width}px + 0.625rem + 1.625rem)`,
          maxWidth: '100%',
          padding: isRtl ? '0 1.5rem 0 0.5rem !important' : '0 0.5rem 0 1.5rem !important',
        }}
        inputProps={{
          className: classes.select,
          style: {
            paddingRight: isRtl ? '0.625rem' : '1rem',
            paddingLeft: isRtl ? '1rem' : '0.625rem',
          },
          ...(props.inputProps || {}),
          id: nativeSelectId,
          // Ensure aria-label survives even if caller passed it at the top level.
          // `title` is a third fallback path (accepted by axe as an accessible name source).
          // `data-a11y-label` is picked up by the global MutationObserver in
          // `enforceAriaLabels.ts` — MUI v4 filters aria-label/title from this path but
          // leaves unknown `data-*` attributes alone, so the observer sees it and writes
          // aria-label onto the native <select> regardless of any MUI re-render.
          ...(callerAriaLabel
            ? { 'aria-label': callerAriaLabel, title: callerAriaLabel, 'data-a11y-label': callerAriaLabel }
            : {}),
          custom: callerAriaLabel,
        }}
        input={<Input fullWidth={fullWidth} />}
        disableUnderline
        fullWidth={fullWidth}
      >
        {children}
      </Select>
    </>
  )
}
